import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { platforms } from '../lib/platforms';
import { initiatePlatformConnection } from '../lib/platformConnections';
import type { Platform } from '../lib/types';

const Integrations = () => {
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState<Record<string, boolean>>({});
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const navigate = useNavigate();

  // Separate platforms by category
  const advertisingPlatforms = platforms.filter(p => p.category === 'advertising');
  const salesPlatforms = platforms.filter(p => p.category === 'sales');

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;

        const { data, error } = await supabase
          .from('platform_connections')
          .select('platform_id, is_active')
          .eq('user_id', session.user.id);

        if (error) throw error;

        const connectionMap = data.reduce((acc, curr) => ({
          ...acc,
          [curr.platform_id]: curr.is_active,
        }), {});

        setConnections(connectionMap);
      } catch (error) {
        console.error('Error fetching connections:', error);
        toast.error('Failed to load platform connections');
      }
    };

    fetchConnections();
  }, []);

  const handleConnect = async (platform: Platform) => {
    try {
      setLoading(true);
      setConnectingPlatform(platform.id);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        toast.error('Please sign in to connect platforms');
        return;
      }

      if (connections[platform.id]) {
        // Handle disconnection
        const { error } = await supabase
          .from('platform_connections')
          .update({ is_active: false })
          .eq('user_id', session.user.id)
          .eq('platform_id', platform.id);

        if (error) throw error;

        setConnections(prev => ({
          ...prev,
          [platform.id]: false,
        }));

        toast.success(`${platform.name} disconnected successfully`);
      } else {
        console.log('Initiating connection for platform:', platform.id);
        // Handle connection
        const result = await initiatePlatformConnection(platform.id, session.user.id);
        
        if (result.requiresConfiguration) {
          toast.error(
            <div>
              <p>This platform is not configured yet.</p>
              <p className="text-sm mt-1">Please contact support to enable this integration.</p>
            </div>,
            { duration: 5000 }
          );
          return;
        }
        
        if (result.error) {
          throw new Error(result.error);
        }
      }
    } catch (error) {
      console.error('Connection error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to connect platform');
    } finally {
      setLoading(false);
      setConnectingPlatform(null);
    }
  };

  const PlatformCard = ({ platform }: { platform: Platform }) => (
    <div className="bg-[#151632]/50 backdrop-blur-sm p-6 rounded-xl border border-[#1E2048] hover:border-purple-500/25 transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">{platform.name}</h3>
          <p className="text-gray-400 text-sm mt-1">{platform.description}</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          platform.category === 'advertising'
            ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
            : 'bg-green-500/10 text-green-400 border border-green-500/20'
        }`}>
          {platform.category === 'advertising' ? 'Advertising' : 'Sales'}
        </div>
      </div>
      <div className="mt-4">
        <button
          onClick={() => handleConnect(platform)}
          disabled={loading}
          className={`w-full py-2 px-4 rounded-lg font-medium transition-all ${
            connections[platform.id]
              ? 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20'
              : 'bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20'
          }`}
        >
          {connections[platform.id] ? 'Disconnect' : 'Connect'}
        </button>
      </div>
    </div>
  );

  const PlatformSection = ({ title, platforms, gradientFrom, gradientTo }: {
    title: string;
    platforms: Platform[];
    gradientFrom: string;
    gradientTo: string;
  }) => (
    <div className="space-y-6">
      <div className="relative">
        <h2 className={`text-2xl font-bold bg-gradient-to-r ${gradientFrom} ${gradientTo} text-transparent bg-clip-text`}>
          {title}
        </h2>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {platforms.map((platform) => (
          <PlatformCard key={platform.id} platform={platform} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-12">
      <PlatformSection
        title="Advertising Platforms"
        platforms={advertisingPlatforms}
        gradientFrom="from-purple-400"
        gradientTo="to-blue-400"
      />
      <PlatformSection
        title="Sales Platforms"
        platforms={salesPlatforms}
        gradientFrom="from-green-400"
        gradientTo="to-emerald-400"
      />
    </div>
  );
};

export default Integrations;